import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";

import { Guid } from "guid-typescript";
import { environment } from "../../../environments/environment";
import { combinePaths } from "../../shared/helpers/file.helper";
import {
  CREATE_STORED_DOCUMENT_REQUEST_CATEGORY_ID,
  CREATE_STORED_DOCUMENT_REQUEST_FILE,
  CREATE_STORED_DOCUMENT_REQUEST_NAME,
  STORED_DOCUMENTS_CATEGORIES_PATH,
  STORED_DOCUMENTS_DOCUMENT_PATH,
  STORED_DOCUMENTS_PATH,
} from "../constants/stored-documents.constants";
import { DocumentBlob } from "../models/document-blob";
import { StoredDocumentCategory } from "../models/stored-document-category.model";
import { StoredDocumentCreationForm } from "../models/stored-document-creation-form.model";
import { StoredDocumentEditionForm } from "../models/stored-document-edition-form.model";
import { StoredDocumentModifyCategoriesForm } from "../models/stored-document-modify-categories-form.model";
import { StoredDocument } from "../models/stored-document.model";
import { StoredDocumentsInformations } from "../models/stored-documents-informations.model";

@Injectable({
  providedIn: "root",
})
export class StoredDocumentsService {
  private readonly endpointUrl = STORED_DOCUMENTS_PATH;
  private readonly baseUrl: string;
  private readonly getDocumentPath: string = STORED_DOCUMENTS_DOCUMENT_PATH;
  private readonly getCategoriesPath: string = STORED_DOCUMENTS_CATEGORIES_PATH;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.apiConfig.uri + this.endpointUrl;
  }

  getStoredDocumentsInformations(policyAgreementId: string): Observable<StoredDocumentsInformations> {
    return this.http.get<StoredDocumentsInformations>(combinePaths(this.baseUrl, policyAgreementId)).pipe(
      // Remap documents to create the Date objects
      map((data: StoredDocumentsInformations) => {
        data.storedDocuments = data.storedDocuments.map(doc => {
          doc.uploadDate = new Date(doc.uploadDate);
          return doc;
        });
        return data;
      })
    );
  }

  createStoredDocument(policyAgreementId: string, form: StoredDocumentCreationForm): Observable<Guid> {
    const formData = new FormData();
    formData.append(CREATE_STORED_DOCUMENT_REQUEST_CATEGORY_ID, form.categoryId.toString());
    formData.append(CREATE_STORED_DOCUMENT_REQUEST_NAME, form.name);
    formData.append(CREATE_STORED_DOCUMENT_REQUEST_FILE, form.file);

    return this.http.post<Guid>(combinePaths(this.baseUrl, policyAgreementId), formData);
  }

  getStoredDocumentContent(policyAgreementId: string, documentId: string): Observable<DocumentBlob> {
    return this.http.get<DocumentBlob>(combinePaths(this.baseUrl, policyAgreementId, this.getDocumentPath, documentId));
  }

  editStoredDocument(policyAgreementId: string, form: StoredDocumentEditionForm): Observable<StoredDocument> {
    return this.http.put<StoredDocument>(
      combinePaths(this.baseUrl, policyAgreementId, this.getDocumentPath, form.documentId),
      { fileName: form.name, categoryId: form.categoryId }
    );
  }

  deleteStoredDocument(policyAgreementId: string, documentId: string): Observable<void> {
    return this.http.delete<void>(combinePaths(this.baseUrl, policyAgreementId, this.getDocumentPath, documentId));
  }

  getStoredDocumentsCategories(policyAgreementId: string): Observable<StoredDocumentCategory[]> {
    return this.http.get<StoredDocumentCategory[]>(
      combinePaths(this.baseUrl, policyAgreementId, this.getCategoriesPath)
    );
  }

  modifyStoredDocumentsCategories(
    policyAgreementId: string,
    form: StoredDocumentModifyCategoriesForm
  ): Observable<StoredDocumentCategory[]> {
    return this.http.put<StoredDocumentCategory[]>(
      combinePaths(this.baseUrl, policyAgreementId, this.getCategoriesPath),
      form
    );
  }
}
